import { WorkspaceResult } from '@/api';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';

type ModalHandlers = {
  handleSave: (selectedWorkspace: WorkspaceResult | null) => void;
  handleCancel: () => void;
};

export default function WorkspaceSelector({
  open,
  setOpen,
  isSaving,
  modalHandlers,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSaving: boolean;
  modalHandlers: ModalHandlers | null;
}) {
  const workspaces = useStore($workspaces);
  const org = useStore($org);
  const [selectedWorkspace, setSelectedWorkspace] =
    useState<WorkspaceResult | null>(null);

  return (
    <AlertDialog
      onOpenChange={opn => {
        if (!opn && modalHandlers) {
          modalHandlers.handleCancel();
        }
        setOpen(opn);
      }}
      open={open}
    >
      <AlertDialogContent className="sm:max-w-[480px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Select workspace</AlertDialogTitle>
          <AlertDialogDescription>
            Some of the nodes requires a workspace to be present. Please select
            a workspace to continue.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid max-h-[240px] gap-4 overflow-y-auto pr-2">
          {workspaces?.length === 0 && (
            <div className="flex justify-start rounded-lg bg-slate-200 p-2 pl-3 pr-3">
              <p className="text-sm text-slate-700">
                It looks like there are no workspaces for this organization yet,
                <Button
                  variant="link"
                  // size="sm"
                  className="mr-1 pl-0 pr-0"
                  onClick={() => {
                    openPage($router, 'workspaces', {
                      organizationId: org!.id,
                    });
                    $sideBarCollapsed.set(false);
                  }}
                >
                  Click here
                </Button>{' '}
                to create a new workspace and get started!
              </p>
            </div>
          )}
          {workspaces?.map(ws => (
            <div
              key={ws?.workspace?.id}
              className={cn(
                selectedWorkspace?.workspace.id === ws.workspace.id &&
                  'ring-primary-muted bg-slate-100 ring-1 ring-inset',
                'flex flex-col gap-1 rounded-lg border border-slate-200 px-3 py-2 hover:cursor-pointer hover:bg-slate-100',
              )}
              onClick={() => setSelectedWorkspace(ws)}
            >
              <div className="flex items-center justify-items-start gap-2">
                <Avatar className="rounded-full bg-slate-200 p-1 text-xs">
                  <AvatarImage
                    src={ws?.workspace?.icon || ''}
                    alt={ws?.workspace?.id}
                  />
                  <AvatarFallback>
                    {ws.workspace.name.slice(0, 2).toLocaleUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <small className="text-sm font-medium leading-none">
                  {ws?.workspace?.name}
                </small>
              </div>
              <div className="flex justify-start">
                <p className="text-sm text-muted-foreground">
                  {ws?.collaborators?.length} Collaborators
                </p>
                <p className="ml-4 text-sm text-muted-foreground">
                  Created on{' '}
                  {new Date(ws.workspace.createdAtUtc).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
        <AlertDialogFooter>
          <Button
            onClick={() => modalHandlers?.handleSave(selectedWorkspace)}
            disabled={!selectedWorkspace || isSaving}
            size="sm"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            onClick={() => {
              if (modalHandlers) {
                modalHandlers.handleCancel();
              }
              setSelectedWorkspace(null);
              setOpen(false);
            }}
            size="sm"
            variant="secondary"
            disabled={isSaving}
          >
            Cancel
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
